import React from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Video, ShieldCheck, Clock, MessageCircle, FileText } from 'lucide-react';

// "What the 20% Service & Warranty covers" section (2026-05-23).
// Sits directly below HowItWorksSection on the homepage. Six-card grid
// (3×2 desktop, 2×2 tablet, 1-col mobile) breaking down what the 20%
// platform fee buys the homeowner. Brand-positioned as the section that
// makes the "open book" claim concrete.
//
// Content decisions (Mike, 2026-05-23):
//   • "5-Year Workmanship Warranty" — shipped per mockup. Current site
//     positions 5-year as the Premium tier (default is 3-year). Other
//     pages (terms-of-service, warranty, how-it-works) will be reconciled
//     in a separate pass to make 5-year the new default.
//   • Customer Support card — "Not a chatbot." line DROPPED from the
//     mockup copy because the site loads a Chatbase widget via the
//     StickyCallTab on every page. Body reads "Real humans, real answers,
//     from address entry through year five of your warranty."
//   • "Manufacturer's 30-50 year material warranty" — shipped verbatim.
//     In-range with the warranty page's "25-year to lifetime limited"
//     phrasing; not contradicted.
//
// Pass 1 styling layered on the structure (this commit):
//   • Light-#F8FAFC section background to differentiate from white
//     neighbors (HowItWorksSection above is white, SanityContentSection
//     below is also typically white).
//   • Card depth + hover lift use the same vocabulary as HowItWorks /
//     RecentInstalls / founders grid — inset cyan ring 12% alpha at rest,
//     intensifies to 35% on hover, 4px lift, cyan glow emerges.
//   • Icon badges: rounded-xl cyan-tinted squares with cyan lucide icons.
//     Subtle inner ring for premium feel.
// Pass 2 (animations / scroll-in choreography) follows.

const CARD_REST_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.12),' +
  ' 0 1px 2px rgba(15, 23, 42, 0.05),' +
  ' 0 10px 28px rgba(15, 23, 42, 0.07)';

const CARD_HOVER_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.35),' +
  ' 0 2px 6px rgba(15, 23, 42, 0.07),' +
  ' 0 4px 12px rgba(15, 23, 42, 0.10),' +
  ' 0 20px 50px rgba(7, 176, 233, 0.18)';

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// ─── Scroll-in motion variants ──────────────────────────────────────────

const sectionV = { hidden: {}, visible: {} };

const eyebrowV = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT_EXPO } },
};

const headlineV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.08, ease: EASE_OUT_EXPO } },
};

const subheadV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, delay: 0.18, ease: EASE_OUT_EXPO } },
};

// Card entrance (parent propagates hidden→visible). Custom index drives
// the per-card stagger so the 6 cards cascade left-to-right, top-to-bottom.
const cardEntranceV = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: 0.30 + i * 0.08, ease: EASE_OUT_EXPO },
  }),
};

// Icon badge: scale-pop with spring overshoot. Fires slightly after each
// card's fade-up so the cyan-tinted icon "lands" into the card as it
// arrives. Same vocabulary as the proof-point icons in TabletDemoSection.
const iconBadgeV = {
  hidden: { scale: 0 },
  visible: (i) => ({
    scale: 1,
    transition: {
      delay: 0.42 + i * 0.08,
      type: 'spring',
      stiffness: 360,
      damping: 18,
    },
  }),
};

const items = [
  {
    Icon: UserPlus,
    title: 'Dedicated Project Manager',
    body: 'One person assigned to your roof. Direct phone. Real-time updates from quote to completion.',
  },
  {
    Icon: Video,
    title: 'Body-Cam Install Supervision',
    body: 'Every crew wears body cameras. Footage stored and reviewable. Accountability you can actually see.',
  },
  {
    Icon: ShieldCheck,
    title: '5-Year Workmanship Warranty',
    body: "Stacks on top of the manufacturer's 30-50 year material warranty. We back the install, the manufacturer backs the shingles.",
  },
  {
    Icon: Clock,
    title: 'Post-Install Inspection',
    body: 'Independent quality check after the crew leaves. Walk-through with you before payment processes.',
  },
  {
    Icon: MessageCircle,
    title: 'Customer Support, Always',
    body: 'Real humans, real answers, from address entry through year five of your warranty.',
  },
  {
    Icon: FileText,
    title: 'Permits, Paperwork, Coordination',
    body: "We pull permits, schedule inspections, file warranties. You don't lift a finger after signing.",
  },
];

const ServiceWarrantySection = () => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    variants={sectionV}
    className="py-16 lg:py-24"
    style={{ background: '#F8FAFC' }}
  >
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8">

      {/* A. Section header — three motion elements staggered via absolute
          delays in their variants (0 / 0.08 / 0.18s). */}
      <div className="text-center mb-12 lg:mb-16">
        <motion.p
          variants={eyebrowV}
          className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
          style={{ color: '#07B0E9' }}
        >
          An open book, down to the last dollar
        </motion.p>
        <motion.h2
          variants={headlineV}
          className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
          style={{
            color: '#000150',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.02em',
          }}
        >
          What the <span style={{ color: '#07B0E9' }}>20% Service &amp; Warranty</span> covers.
        </motion.h2>
        <motion.p
          variants={subheadV}
          className="text-gray-500 text-base lg:text-lg max-w-3xl mx-auto leading-relaxed"
        >
          Real transparency means showing you where every dollar goes. The 20% flat Service &amp; Warranty fee covers manufacturer-certified installation, dedicated project management, and your 5-year workmanship warranty — replacing every hidden markup, commission, and middleman charge in traditional roofing.
        </motion.p>
      </div>

      {/* B. 6-card grid.
            Desktop (lg+): 3 cols × 2 rows — the canonical visual.
            Tablet (sm/md): 2 cols × 3 rows.
            Mobile (default): single column stack.
          Each card fades up with a per-index stagger (0.30 + i*0.08s);
          the cyan icon badge inside spring-pops shortly after the card
          settles. Hover lift + shadow brightening preserved as inline
          onMouseEnter/Leave handlers — kept separate from the variant
          system so the entrance variants and the hover effect don't
          fight over the same animated properties. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {items.map(({ Icon, title, body }, i) => (
          <motion.article
            key={title}
            variants={cardEntranceV}
            custom={i}
            className="bg-white rounded-2xl p-6 lg:p-7 flex flex-col"
            style={{
              boxShadow: CARD_REST_SHADOW,
              transition: 'box-shadow 350ms cubic-bezier(0.16, 1, 0.3, 1), transform 350ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = CARD_HOVER_SHADOW;
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = CARD_REST_SHADOW;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* Icon badge — spring-scales in from 0 once the card has
                started fading up. Same vocabulary as TabletDemoSection's
                proof-point icons. */}
            <motion.div
              variants={iconBadgeV}
              custom={i}
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 flex-shrink-0"
              style={{
                background: 'rgba(7, 176, 233, 0.10)',
                boxShadow: 'inset 0 0 0 1px rgba(7, 176, 233, 0.18)',
              }}
              aria-hidden="true"
            >
              <Icon size={22} strokeWidth={2} style={{ color: '#07B0E9' }} />
            </motion.div>

            <h3
              className="text-lg lg:text-xl font-bold mb-2 leading-snug"
              style={{ color: '#000150' }}
            >
              {title}
            </h3>

            <p className="text-sm lg:text-[15px] text-gray-600 leading-relaxed">
              {body}
            </p>
          </motion.article>
        ))}
      </div>

    </div>
  </motion.section>
);

export default ServiceWarrantySection;
